import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'finops' | 'studio' | 'outline';
  size?:    'sm' | 'md' | 'lg';
  children: ReactNode;
}

const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  finops:  'bg-finops text-black hover:opacity-90',
  studio:  'bg-studio text-black hover:opacity-90',
  outline: 'border border-border text-foreground hover:bg-muted',
};

const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-5 py-2.5',
  lg: 'px-6 py-3 text-lg',
};

/**
 * Primary CTA button — used identically across finops / studio / id.
 * Accent is driven by tokens (`bg-primary`, `bg-finops`, `bg-studio`) so
 * per-product theming stays in one place.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={clsx(
        'rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
